const RAW_BASE = process.env.GATSBY_REVIEWS_API_URL || ""
const REVIEWS_BASE = RAW_BASE.replace(/\/+$/, "")
const TURNSTILE_SITE_KEY = process.env.GATSBY_TURNSTILE_SITE_KEY || ""

const MAX_FILE_SIZE = 5 * 1024 * 1024
const MAX_PHOTOS = 3
const MAX_VIDEOS = 1
const MAX_PAGES = 10
const PAGE_LIMIT = 50

const IMAGE_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif"]
const VIDEO_MIME = ["video/mp4", "video/webm", "video/quicktime"]

const FIT_ENUM = ["too_small", "slightly_small", "perfect", "slightly_big", "too_big"]

export const FIT_LABELS = [
    "Runs small",
    "Runs slightly small",
    "Fits true to size",
    "Runs slightly large",
    "Runs large",
]

export const isTurnstileEnabled = () => TURNSTILE_SITE_KEY !== ""

export const getTurnstileSiteKey = () => TURNSTILE_SITE_KEY

export const fitIndexToEnum = (index) => FIT_ENUM[Math.max(0, Math.min(4, index))]

export const safeUrl = (raw) => {
    if (!raw) return "#"
    try {
        const origin = typeof window !== "undefined" ? window.location.origin : "https://blackboughswim.com"
        const url = new URL(raw, origin)
        return ["http:", "https:", "mailto:"].includes(url.protocol) ? raw : "#"
    } catch {
        return "#"
    }
}

const targetParams = (target) => {
    const collection = target?.collection?.trim()
    if (collection) return { collection }
    return { product_id: String(target?.productId ?? "") }
}

const hasTarget = (target) => !!(target?.collection || target?.productId)

export const getSummary = async (target) => {
    const fallback = { total: 0, average_rating: 0, fit: null }
    if (!REVIEWS_BASE || !hasTarget(target)) return fallback
    try {
        const params = new URLSearchParams(targetParams(target))
        const res = await fetch(`${REVIEWS_BASE}/api/reviews/summary?${params.toString()}`)
        if (!res.ok) return fallback
        return await res.json()
    } catch (error) {
        console.error("Error fetching review summary:", error?.message)
        return fallback
    }
}

export const getReviewsPage = async ({ target, page = 1, limit = PAGE_LIMIT, sort = "recent" }) => {
    const fallback = { reviews: [], page, limit, total: 0, total_pages: 1 }
    if (!REVIEWS_BASE || !hasTarget(target)) return fallback
    try {
        if (target?.collection) target.collection = target.collection.replace("'", "")
        const params = new URLSearchParams({
            ...targetParams(target),
            page: String(page),
            limit: String(limit),
            sort,
        })
        const res = await fetch(`${REVIEWS_BASE}/api/reviews?${params.toString()}`)
        if (!res.ok) return fallback
        return await res.json()
    } catch (error) {
        console.error("Error fetching reviews:", error?.message)
        return fallback
    }
}

export const WALL_PAGE_LIMIT = 48

export const getReviewsWall = async ({ page = 1, limit = WALL_PAGE_LIMIT, sort } = {}) => {
    const fallback = { reviews: [], page, limit, total: 0, total_pages: 1 }
    if (!REVIEWS_BASE) return fallback
    try {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) })
        params.set("region", process.env.GATSBY_STORE === "PH" ? "PH" : "US")
        if (sort === "highest_rated" || sort === "lowest_rated") params.set("sort", sort)
        const res = await fetch(`${REVIEWS_BASE}/api/reviews?${params.toString()}`)
        if (!res.ok) return fallback
        return await res.json()
    } catch (error) {
        console.error("Error fetching reviews wall:", error?.message)
        return fallback
    }
}

export const GARMENT_TYPES = ["Top", "Bottoms", "One Piece", "Swim Shorts", "Board Shorts"]

export const getStyleWord = (title) =>
    String(title || "").split(" / ")[0].trim().split(/\s+/)[0] || ""

export const getGarmentType = (title) => {
    const name = String(title || "").split(" / ")[0].trim().toLowerCase()
    const ordered = [...GARMENT_TYPES].sort((a, b) => b.length - a.length)
    return ordered.find((type) => name.endsWith(type.toLowerCase())) || null
}

export const getStyleCollectionHandle = (title, type) =>
    `${getStyleWord(title)} ${type}`.trim().toLowerCase().replace(/\s+/g, "-")

export const getAllReviews = async (target) => {
    const first = await getReviewsPage({ target, page: 1 })
    const collected = [...(first?.reviews || [])]
    const totalPages = Math.min(first?.total_pages || 1, MAX_PAGES)

    for (let page = 2; page <= totalPages; page++) {
        const next = await getReviewsPage({ target, page })
        collected.push(...(next?.reviews || []))
    }

    if ((first?.total_pages || 1) > MAX_PAGES) {
        const label = target?.collection ? `collection ${target.collection}` : `product ${target?.productId}`
        console.warn(`Review pagination capped at ${MAX_PAGES} pages for ${label}`)
    }

    return collected.map(normalizeReview)
}

export const normalizeReview = (r) => ({
    id: r?.id,
    review: {
        rating: r?.rating,
        title: r?.title,
        body: r?.body,
        author: r?.author_name,
        region: r?.region,
        productTitle: r?.product?.title,
        dateCreated: r?.created_at,
        verified: r?.verification?.verified,
    },
    media: Array.isArray(r?.media) ? r.media : [],
    attributes: Array.isArray(r?.attributes) ? r.attributes : [],
})

export const buildRatingDistribution = (normalized) => {
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }

    normalized?.forEach((item) => {
        const rating = Number(item?.review?.rating)
        if (counts[rating] !== undefined) counts[rating]++
    })

    const ratings = Object.entries(counts).map(([star, total]) => ({
        star: Number(star),
        total,
    }))

    return { ratings }
}

export const getFit = (summary) => {
    if (!summary?.fit || summary.fit.average_index == null) return null
    const activeIndex = Math.max(0, Math.min(4, Math.round(summary.fit.average_index)))
    return {
        activeIndex,
        label: summary.fit.label || FIT_LABELS[activeIndex],
        responses: summary.fit.responses || 0,
    }
}

export const validateMedia = (files) => {
    const list = Array.isArray(files) ? files : []
    if (list.length === 0) return { ok: true }

    const images = list.filter((f) => IMAGE_MIME.includes(f?.type))
    const videos = list.filter((f) => VIDEO_MIME.includes(f?.type))

    if (images.length + videos.length !== list.length) {
        return { ok: false, error: "Unsupported file type. Use JPG, PNG, WEBP, GIF, MP4, WEBM or MOV." }
    }
    if (images.length > 0 && videos.length > 0) {
        return { ok: false, error: "Please upload either photos or a video, not both." }
    }
    if (images.length > MAX_PHOTOS) {
        return { ok: false, error: `You can upload up to ${MAX_PHOTOS} photos.` }
    }
    if (videos.length > MAX_VIDEOS) {
        return { ok: false, error: `You can upload up to ${MAX_VIDEOS} video.` }
    }
    if (list.some((f) => f?.size > MAX_FILE_SIZE)) {
        return { ok: false, error: "Each file must be under 5MB." }
    }

    return { ok: true }
}

export const submitReview = async ({
    productId,
    rating,
    body,
    title,
    authorName,
    authorEmail,
    region = "US",
    fit,
    media,
    turnstileToken,
}) => {
    if (!REVIEWS_BASE) {
        throw Object.assign(new Error("Reviews API is not configured."), { code: "not_configured" })
    }

    const fd = new FormData()
    fd.set("product_id", String(productId))
    fd.set("rating", String(rating))
    fd.set("body", body)
    if (title) fd.set("title", title)
    fd.set("author_name", authorName)
    if (authorEmail) fd.set("author_email", authorEmail)
    fd.set("region", region)
    if (fit) fd.set("fit", fit)
    for (const file of media || []) fd.append("media", file, file.name)
    if (turnstileToken) fd.set("cf-turnstile-response", turnstileToken)

    const res = await fetch(`${REVIEWS_BASE}/api/reviews`, {
        method: "POST",
        body: fd,
    })

    let data = null
    try {
        data = await res.json()
    } catch {
        data = null
    }

    if (!res.ok) {
        const code = data?.error || "empty"
        throw Object.assign(new Error(code), { code, field: data?.field, status: res.status })
    }

    return data
}
